import { cn } from '@up2stay/up2promo-ui';

import {
  SettingsGroup,
  SettingsGroupFooter,
  SettingsGroupLabel,
  SETTINGS_GROUP_LABEL_CLASS,
} from '@/core/components/settings/settings-group';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';

type SettingsSectionProps = {
  id: string;
  title: string;
  /**
   * Legacy under-title paragraph. Prefer `footer` (below group) or `info`
   * (popover). When `grouped`, `description` is treated as `footer` unless
   * `footer` is set explicitly.
   */
  description?: string;
  /** Small muted copy below the grouped card (iOS Settings pattern). */
  footer?: string;
  /** Near-title help via info icon + popover. */
  info?: string;
  /** Wrap children in SettingsGroup card surface. */
  grouped?: boolean;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  tone?: 'default' | 'danger';
  className?: string;
};

/** Profiles SoT section title (`--t-h3`) for non-grouped settings zones. */
export const SETTINGS_SECTION_TITLE_CLASS =
  'text-[15.5px] font-bold leading-tight text-foreground';

/** Profiles SoT muted body (`--t-body`) — prefer footer/info over this for density. */
export const SETTINGS_SECTION_DESCRIPTION_CLASS =
  'max-w-prose text-[13.5px] leading-relaxed text-muted-foreground';

/**
 * Settings zone: label + optional info, grouped card, optional footer.
 * Phase 7 density: avoid essay paragraphs inside interactive rows.
 */
export function SettingsSection({
  id,
  title,
  description,
  footer,
  info,
  grouped = false,
  actions,
  children,
  tone = 'default',
  className,
}: SettingsSectionProps) {
  const footerText = footer ?? (grouped ? description : undefined);
  const showLegacyDescription =
    !grouped && footer == null && info == null && Boolean(description);
  const hasChildren = children != null && children !== false;

  return (
    <section
      id={id}
      className={cn('scroll-mt-4 flex flex-col gap-2', className)}
      aria-labelledby={`${id}-title`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex items-center gap-0.5">
          {grouped ? (
            <SettingsGroupLabel
              id={`${id}-title`}
              className={cn(tone === 'danger' && 'text-destructive')}
            >
              {title}
            </SettingsGroupLabel>
          ) : (
            <h2
              id={`${id}-title`}
              className={cn(
                SETTINGS_SECTION_TITLE_CLASS,
                tone === 'danger' && 'text-destructive',
              )}
            >
              {title}
            </h2>
          )}
          {info ? (
            <SettingsInfoHint label={title}>{info}</SettingsInfoHint>
          ) : null}
        </div>
        {actions ? (
          <div className="flex shrink-0 items-center gap-2">{actions}</div>
        ) : null}
      </div>
      {showLegacyDescription ? (
        <p className={SETTINGS_SECTION_DESCRIPTION_CLASS}>{description}</p>
      ) : null}
      {grouped && hasChildren ? (
        <SettingsGroup tone={tone}>{children}</SettingsGroup>
      ) : hasChildren ? (
        <div className={cn(tone === 'danger' && 'flex flex-col gap-3')}>
          {children}
        </div>
      ) : null}
      {footerText ? (
        <SettingsGroupFooter>{footerText}</SettingsGroupFooter>
      ) : null}
    </section>
  );
}

/** Re-export for callers that only need the label class token. */
export { SETTINGS_GROUP_LABEL_CLASS };
